import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, ReferenceDot, ResponsiveContainer } from 'recharts'
import { cn } from '@/lib/utils'
import type { DailySeriesPoint } from '@/types/teamPerformance'

interface Props {
  data: DailySeriesPoint[]
  /** Couleur dominante (CSS color). Tailwind ne s'applique pas aux SVG recharts. */
  color: string
  height?: number
  /** Locale pour formater la date du pic. Défaut 'fr'. */
  locale?: string
  /** Affiche l'annotation "Pic le X (N)" sous la courbe. Désactivable pour les
   *  sparklines compactes ou les séries où le pic n'a pas de sens. */
  showPeakAnnotation?: boolean
  /** Affiche un dot coloré sur le point de pic. */
  showPeakDot?: boolean
  className?: string
  /** Couleur du texte annotation (selon dark mode). */
  textColor?: string
}

/**
 * Sparkline — silhouette de la dynamique sur la période sélectionnée.
 *
 * Identité Callizy : on assume la sparkline dans les cartes hero, là où
 * Cloudtalk/JustCall mettent un grand graphique séparé. Notre persona SMB
 * regarde 30 sec, doit tout voir d'un coup.
 *
 * - Downsampling auto au-delà de 60 points (silhouette lisible sur 12 mois).
 * - Dot coloré sur le pic + annotation "Pic le 12 mai (87)" sous la courbe.
 * - Ligne plate discrète quand tous les points sont à zéro (préserve rythme grille).
 */
export function Sparkline({
  data,
  color,
  height = 48,
  locale = 'fr',
  showPeakAnnotation = true,
  showPeakDot = true,
  className,
  textColor,
}: Props) {
  const { t } = useTranslation()
  const downsampled = useMemo(() => downsample(data, 60), [data])
  const peak = useMemo(() => findPeak(downsampled), [downsampled])
  const allZero = downsampled.every((p) => p.value === 0)
  const gradientId = useMemo(
    () => `spark-${color.replace(/[^a-z0-9]/gi, '')}-${Math.round(height)}`,
    [color, height],
  )

  if (downsampled.length < 2) {
    return <div style={{ height }} className={className} aria-hidden />
  }

  if (allZero) {
    return (
      <div style={{ height }} className={className} aria-hidden>
        <div className="h-full flex items-center">
          <div
            className="w-full"
            style={{ height: 1, backgroundColor: color, opacity: 0.2 }}
          />
        </div>
      </div>
    )
  }

  const peakLabel =
    peak && showPeakAnnotation
      ? t('manager.analytics.sparklinePeak', {
          date: formatPeakDate(peak.date, locale),
          value: peak.value.toLocaleString(),
        })
      : null

  return (
    <div className={cn('flex flex-col', className)} aria-hidden>
      <div style={{ height, width: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={downsampled} margin={{ top: 4, right: 0, left: 0, bottom: 2 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={1.75}
              fill={`url(#${gradientId})`}
              isAnimationActive={false}
              dot={false}
            />
            {showPeakDot && peak && (
              <ReferenceDot
                x={peak.date}
                y={peak.value}
                r={3.5}
                fill={color}
                stroke="#ffffff"
                strokeWidth={1.5}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {peakLabel && (
        <span
          className={cn(
            'text-[10px] mt-0.5 leading-tight tabular-nums truncate',
            textColor || 'text-slate-500 dark:text-slate-400',
          )}
        >
          {peakLabel}
        </span>
      )}
    </div>
  )
}

/** Retourne le point de pic (max value, jour le plus récent en cas d'égalité). */
function findPeak(points: DailySeriesPoint[]): DailySeriesPoint | null {
  if (!points.length) return null
  let best = points[0]
  for (const p of points) {
    if (p.value > best.value || (p.value === best.value && p.date > best.date)) {
      best = p
    }
  }
  return best.value > 0 ? best : null
}

/** Format court "12 mai" / "May 12" selon la locale. */
function formatPeakDate(iso: string, locale: string): string {
  const d = new Date(iso + 'T12:00:00') // midi pour éviter les drift TZ
  return d.toLocaleDateString(locale === 'fr' ? 'fr-CA' : 'en-CA', {
    day: 'numeric',
    month: 'short',
  })
}

/** Réduit `points` à au plus `maxPoints` en moyennant par buckets. */
function downsample(points: DailySeriesPoint[], maxPoints: number): DailySeriesPoint[] {
  if (points.length <= maxPoints) return points
  const bucketSize = Math.ceil(points.length / maxPoints)
  const out: DailySeriesPoint[] = []
  for (let i = 0; i < points.length; i += bucketSize) {
    const slice = points.slice(i, i + bucketSize)
    const avg = slice.reduce((s, p) => s + p.value, 0) / slice.length
    out.push({ date: slice[0].date, value: Math.round(avg) })
  }
  return out
}
